import { useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

export interface SelectedOrderInfo {
  orderId: string;
  orderCode: string;
}

interface OrderOption {
  value: string;
  label: string;
  orderId: string;
  orderCode: string;
}

interface SelectOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (info: SelectedOrderInfo) => void;
  onCancel?: () => void;
}

export function SelectOrderDialog({ open, onOpenChange, onConfirm, onCancel }: SelectOrderDialogProps) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<OrderOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<OrderOption | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setOptions([]);
      setSelected(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (query.length < 2) {
      setOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      setIsLoading(true);
      try {
        const { data } = await supabase
          .from('order_overview')
          .select('id, order_code, customer_name')
          .or(`order_code.ilike.%${query}%,customer_name.ilike.%${query}%`)
          .limit(20);
        setOptions(
          (data || []).map((o: any) => ({
            value: o.order_code,
            label: `${o.order_code} — ${o.customer_name || ''}`,
            orderId: o.id,
            orderCode: o.order_code,
          })),
        );
      } catch (e) {
        console.error('Error searching orders:', e);
      } finally {
        setIsLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, open]);

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm({ orderId: selected.orderId, orderCode: selected.orderCode });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleCancel();
        else onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>เลือก Order ก่อนปิดการขาย</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>ค้นหา Order <span className="text-red-500">*</span></Label>
          <ReactSelect
            value={selected}
            onChange={(opt) => setSelected(opt as OrderOption | null)}
            onInputChange={(v) => setQuery(v)}
            options={options}
            isLoading={isLoading}
            placeholder="พิมพ์ order code หรือชื่อลูกค้า..."
            isClearable
            noOptionsMessage={() => (query.length < 2 ? 'พิมพ์อย่างน้อย 2 ตัวอักษร' : 'ไม่พบ order')}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <p className="text-xs text-muted-foreground">
            ต้องเลือก Order ก่อนถึงจะเปลี่ยนสถานะเป็น "ปิดการขายเรียบร้อย" ได้
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>ยกเลิก</Button>
          <Button onClick={handleConfirm} disabled={!selected}>ยืนยัน</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}